// A cm-chessboard, driven through `Surface`: positions, marks, hints and
// whether it takes a move. Every board in the app is one of these; what
// differs between them is what decides the moves (a puzzle, the bot, a
// repertoire), not how the board is drawn.

import { Chessboard, COLOR, INPUT_EVENT_TYPE, type ArrowType, type InputEvent, type MarkerType } from "cm-chessboard/src/Chessboard.js";
import { Markers } from "cm-chessboard/src/extensions/markers/Markers.js";
import { Arrows } from "cm-chessboard/src/extensions/arrows/Arrows.js";
import "cm-chessboard/assets/chessboard.css";
import standardPieces from "cm-chessboard/assets/pieces/standard.svg?url";
import stauntyPieces from "cm-chessboard/assets/pieces/staunty.svg?url";
import markersSprite from "cm-chessboard/assets/extensions/markers/markers.svg?url";
import arrowsSprite from "cm-chessboard/assets/extensions/arrows/arrows.svg?url";
import standardSvg from "cm-chessboard/assets/pieces/standard.svg?raw";
import stauntySvg from "cm-chessboard/assets/pieces/staunty.svg?raw";
import markersSvg from "cm-chessboard/assets/extensions/markers/markers.svg?raw";
import arrowsSvg from "cm-chessboard/assets/extensions/arrows/arrows.svg?raw";
import "./board.css";
import type { HintLevel, Mark, Surface } from "./puzzle.ts";

export const BOARD_STYLES = ["default", "default-contrast", "green", "blue", "chess-club", "chessboard-js", "black-and-white"] as const;
export const PIECE_SETS = { standard: standardPieces, staunty: stauntyPieces } as const;
export const MARKERS_SPRITE = markersSprite;

export type BoardStyle = (typeof BOARD_STYLES)[number];
export type PieceSet = keyof typeof PIECE_SETS;

const PIECE_SVGS: Record<PieceSet, string> = { standard: standardSvg, staunty: stauntySvg };

/**
 * The sprites a board draws with, put on the page from the app's own code.
 *
 * cm-chessboard keeps each sprite in a hidden element and fetches it the
 * first time a board is made — a round trip after the board is already on
 * the screen, with the pieces missing until it returns. Put there before the
 * board is made, the element is found and nothing is fetched. The pieces'
 * element holds one set, the one chosen now, so a set changed in Settings is
 * the one the next board draws.
 */
function primeSprites(pieces: PieceSet): void {
  const put = (id: string, svg: string, set: string) => {
    let wrapper = document.getElementById(id);
    if (wrapper && wrapper.dataset.set === set) return;
    if (!wrapper) {
      wrapper = document.createElement("div");
      wrapper.id = id;
      wrapper.style.transform = "scale(0)";
      wrapper.style.position = "absolute";
      wrapper.setAttribute("aria-hidden", "true");
      document.body.appendChild(wrapper);
    }
    wrapper.dataset.set = set;
    wrapper.innerHTML = svg;
  };
  put("cm-chessboard-sprite", PIECE_SVGS[pieces], pieces);
  put("cm-chessboard-markers", markersSvg, "markers");
  put("cm-chessboard-arrows", arrowsSvg, "arrows");
}

/** How long a piece takes to move. Everything that waits for a board to
 *  settle waits for this. */
export const ANIMATION_MS = 300;

// Marker types are matched by identity when they are removed, so each is one
// object, made once.
const MARKS: Record<ViewMark, MarkerType> = {
  good: { class: "good-square", slice: "markerSquare" },
  source: { class: "source-square", slice: "markerSquare" },
  bad: { class: "bad-square", slice: "markerSquare" },
  check: { class: "check-square", slice: "markerSquare" },
  last: { class: "last-move", slice: "markerSquare" },
  // The move a line wants, against the move that keeps being played.
  wanted: { class: "good-move", slice: "markerSquare" },
  played: { class: "bad-move", slice: "markerSquare" },
};

/** Every mark a board can wear: a puzzle's, and a board that is looked at. */
export type ViewMark = Mark | "last" | "wanted" | "played";

// A hint shows several moves that all answer, told apart by shade: the same
// green, lighter each time, so "any of these" reads at a glance.
const HINT_SHADES = 3;
const HINT_SQUARES: MarkerType[] = Array.from({ length: HINT_SHADES }, (_, i) => ({ class: `hint-square hint-shade-${i}`, slice: "markerSquare" }));
const HINT_ARROWS: ArrowType[] = Array.from({ length: HINT_SHADES }, (_, i) => ({ class: `hint-arrow hint-shade-${i}` }));

// The moves a line offers from a position, drawn as arrows: green for your
// own, red for the replies you are prepared for, which are the colours a
// right move and a wrong one wear on every board of the app. A shade each,
// so that two moves of one piece can be told apart, and every move offered is
// drawn: past the last shade they start again from the first.
export const REPLY_SHADES = 8;
/** Your line's own moves and the replies it is prepared for, and beside them
 *  what the book knows that the line does not: a rare move of your own that
 *  scores well, and a common one of theirs that does not. Those two shimmer,
 *  being nobody's habit and nobody's preparation. */
export type Tone = "line" | "reply" | "sly" | "trap";
const TONES: Tone[] = ["line", "reply", "sly", "trap"];
const arrows = (faded: boolean) =>
  Object.fromEntries(
    TONES.map((tone) => [tone, Array.from({ length: REPLY_SHADES }, (_, i) => ({ class: `reply-arrow ${tone}-shade-${i}${faded ? " reply-faded" : ""}` }))])
  ) as Record<Tone, ArrowType[]>;
const ARROWS = arrows(false);
const FADED = arrows(true);

export interface BoardOptions {
  fen: string;
  orientation: "white" | "black";
  style?: string;
  pieces?: string;
  /** A piece is picked up: whether it may be. */
  onPick?(square: string): boolean;
  /** It is put down, or `to` is null for back where it was: whether the
   *  move stands. */
  onDrop?(from: string, to: string | null): boolean;
}

export class BoardView implements Surface {
  private board: Chessboard;
  private input: "solver" | "free" | null = null;
  private applied: "solver" | "free" | null = null;
  /** Between picking a piece up and putting it down. The board is not told
   *  to stop taking moves in the middle of one: it would drop the piece. */
  private holding = false;
  private destroyed = false;
  private options: BoardOptions;

  constructor(el: HTMLElement, options: BoardOptions) {
    this.options = options;
    const style = BOARD_STYLES.includes(options.style as BoardStyle) ? options.style : "default";
    const set: PieceSet = (options.pieces as PieceSet) in PIECE_SETS ? (options.pieces as PieceSet) : "standard";
    const pieces = PIECE_SETS[set];
    primeSprites(set);
    this.board = new Chessboard(el, {
      position: options.fen,
      orientation: options.orientation === "black" ? COLOR.black : COLOR.white,
      responsive: true,
      style: {
        cssClass: style,
        // Files and ranks around the board: naming the square you are looking
        // at is most of how people talk and think about a position.
        showCoordinates: true,
        pieces: { file: pieces },
        animationDuration: ANIMATION_MS,
      },
      extensions: [
        { class: Markers, props: { autoMarkers: null, sprite: markersSprite } },
        { class: Arrows, props: { sprite: arrowsSprite } },
      ],
    });
  }

  setPosition(fen: string, animate: boolean): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    return this.board.setPosition(fen, animate);
  }

  /**
   * The board drawn as the position it holds, once what it was drawing has
   * finished. A move made by hand while an earlier position was still being
   * animated can leave the pieces drawn as a snapshot from before the move,
   * though the board knows better: this puts the drawing right.
   */
  async settle(fen: string): Promise<void> {
    if (this.destroyed) return;
    await this.board.setPosition(fen, false);
    if (this.destroyed) return;
    (this.board as unknown as { view?: { redrawPieces(): void } }).view?.redrawPieces();
  }

  setOrientation(side: "white" | "black"): void {
    if (!this.destroyed) void this.board.setOrientation(side === "black" ? COLOR.black : COLOR.white);
  }

  mark(mark: ViewMark, squares: string[]): void {
    if (this.destroyed) return;
    for (const square of squares) this.board.addMarker(MARKS[mark], square);
  }

  unmark(mark?: ViewMark): void {
    if (this.destroyed) return;
    if (mark) {
      this.board.removeMarkers(MARKS[mark]);
      return;
    }
    for (const type of Object.values(MARKS)) this.board.removeMarkers(type);
    this.clearHint();
  }

  private clearHint(): void {
    for (const type of HINT_SQUARES) this.board.removeMarkers(type);
    for (const type of HINT_ARROWS) this.board.removeArrows(type);
  }

  hint(level: HintLevel, moves: string[]): void {
    if (this.destroyed) return;
    this.clearHint();
    if (level === "clear") return;
    if (level === "arrow") {
      moves.slice(0, HINT_SHADES).forEach((uci, i) => this.board.addArrow(HINT_ARROWS[i]!, uci.slice(0, 2), uci.slice(2, 4)));
      return;
    }
    // Two moves of one piece are one piece: marked once, in the first shade.
    const pieces = moves.map((uci) => uci.slice(0, 2)).filter((sq, i, all) => all.indexOf(sq) === i);
    pieces.slice(0, HINT_SHADES).forEach((square, i) => this.board.addMarker(HINT_SQUARES[i]!, square));
  }

  /** The moves offered from here, an arrow each; none clears. `bright`, when
   *  given, is the moves in play — a piece picked, or a square with several
   *  arrows on it — and the rest are faded. */
  replies(moves: { uci: string; tone: Tone }[], bright: string[] | null = null): void {
    if (this.destroyed) return;
    for (const tone of TONES) for (const type of [...ARROWS[tone], ...FADED[tone]]) this.board.removeArrows(type);
    // Longest first: e7-e5 under e7-e6, not over it, where two replies leave
    // from one square.
    const length = (uci: string) => Math.hypot(uci.charCodeAt(2) - uci.charCodeAt(0), uci.charCodeAt(3) - uci.charCodeAt(1));
    // Each tone counts its own shades: a line's first move and the first
    // rare one are each the first of theirs.
    const shade = new Map<Tone, number>();
    moves
      .map((move) => ({ ...move, i: (shade.set(move.tone, (shade.get(move.tone) ?? -1) + 1).get(move.tone)! % REPLY_SHADES) }))
      .sort((a, b) => length(b.uci) - length(a.uci))
      .forEach(({ uci, tone, i }) => {
        const faded = bright !== null && !bright.includes(uci);
        this.board.addArrow((faded ? FADED : ARROWS)[tone][i]!, uci.slice(0, 2), uci.slice(2, 4));
      });
  }

  setInput(input: "solver" | "free" | null): void {
    this.input = input;
    if (!this.holding) this.applyInput();
  }

  private applyInput(): void {
    if (this.destroyed || this.applied === this.input) return;
    // A gesture the board still thinks is under way keeps its listeners on
    // the whole window — touchend among them — after move input is turned
    // off, and on an iPhone the next tap anywhere, Undo's, ran into them.
    // Turned off or changed, the board lets go of any gesture first.
    try {
      (this.board as unknown as { cancelMoveInput?: () => void }).cancelMoveInput?.();
    } catch {
      // Nothing in progress, or a board already gone.
    }
    this.board.disableMoveInput();
    this.applied = this.input;
    if (this.input) this.board.enableMoveInput((event) => this.onInput(event));
  }

  private onInput(event: InputEvent): boolean {
    switch (event.type) {
      case INPUT_EVENT_TYPE.moveInputStarted: {
        const may = this.options.onPick ? this.options.onPick(event.squareFrom) : false;
        this.holding = may;
        return may;
      }
      case INPUT_EVENT_TYPE.validateMoveInput:
        return this.options.onDrop ? this.options.onDrop(event.squareFrom, event.squareTo || null) : false;
      case INPUT_EVENT_TYPE.moveInputCanceled:
        this.options.onDrop?.(event.squareFrom, null);
        this.release();
        return true;
      case INPUT_EVENT_TYPE.moveInputFinished:
        this.release();
        return true;
      default:
        return true;
    }
  }

  /** The piece is down: whatever the move changed about the board taking
   *  moves applies now. */
  private release(): void {
    this.holding = false;
    setTimeout(() => this.applyInput(), 0);
  }

  destroy(): void {
    this.destroyed = true;
    try {
      this.board.destroy();
    } catch {
      // Already gone with its element.
    }
  }
}
